import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  external_email_type,
  external_email_status,
  purchasing_email_status,
  ExternalEmailIntegrationType,
  ExternalEmailType,
  ExternalEmailStatus
} from 'views/companies/settings/external-email-integration/config';
import { FieldsStateType } from '@/common/types/form';
import { getOptionValue } from 'views/companies/settings/helpers';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Select from '@/library/components/form/inputs/select';
import Switch from '@/library/components/form/inputs/Switch';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatBooleanDisplay, formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type ExternalEmailIntegrationProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.external_email;
const CATEGORY_TITLE = 'External Email Integration';

const TYPE_OPTIONS = [
  { label: 'wingu_email', value: 'wingu_email' },
  { label: 'infogate_email', value: 'infogate_email' }
];

const STATUS_OPTIONS = [
  { label: 'Disabled', value: 'disabled' },
  { label: 'Enabled', value: 'enabled' }
];

const LABELS = {
  [external_email_type]: 'Integration Name',
  [external_email_status]: 'Integration Status',
  [purchasing_email_status]: 'Purchasing email status'
};

const ExternalEmailIntegration: FC<ExternalEmailIntegrationProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<ExternalEmailIntegrationType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [external_email_type]: companySettings.external_email_type,
      [external_email_status]: companySettings.external_email_status,
      [purchasing_email_status]: companySettings.purchasing_email_status === 'enabled'
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) =>
      collectValues(validate) as ExternalEmailIntegrationType | null,
    toPayload: values => [
      {
        key: external_email_type,
        value: getOptionValue(values[external_email_type])
      },
      {
        key: external_email_status,
        value: getOptionValue(values[external_email_status])
      },
      {
        key: purchasing_email_status,
        value: values[purchasing_email_status] ? 'enabled' : 'disabled'
      }
    ],
    labels: LABELS,
    formatValue: (key, value) => {
      if (key === external_email_type) return formatOptionDisplay(value, TYPE_OPTIONS);
      if (key === external_email_status) return formatOptionDisplay(value, STATUS_OPTIONS);
      return formatBooleanDisplay(value, 'On', 'Off');
    },
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        title="Integration"
        hidden={
          !isSettingVisible(CATEGORY_ID, external_email_type) &&
          !isSettingVisible(CATEGORY_ID, external_email_status) &&
          !isSettingVisible(CATEGORY_ID, purchasing_email_status)
        }
        className="cpb-2 cmb-2"
      >
        <Row className="d-flex align-items-center gx-1">
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={external_email_type}>
              <Select
                className="mb-0"
                label={<InputLabel title="INTEGRATION NAME" apiKey={external_email_type} />}
                options={TYPE_OPTIONS}
                placeholder="Select name"
                onChange={(option: ExternalEmailType) => selectField(external_email_type)(option)}
                isClearable
                {...fields[external_email_type]}
              />
            </VisibleSetting>
          </Col>
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={external_email_status}>
              <Select
                className="mb-0"
                label={<InputLabel title="INTEGRATION STATUS" apiKey={external_email_status} />}
                options={STATUS_OPTIONS}
                onChange={(option: ExternalEmailStatus) =>
                  selectField(external_email_status)(option)
                }
                {...fields[external_email_status]}
              />
            </VisibleSetting>
          </Col>
          <Col xs={3} className="d-flex align-items-center align-self-end h-24">
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={purchasing_email_status}>
              <div className="d-flex align-items-center">
                <Switch
                  className="mb-0"
                  css={css`
                    .slide-label {
                      text-transform: none;
                    }
                  `}
                  size="sm"
                  label="Purchasing email status"
                  switchClassName="turquoise-switch"
                  labelClassName="cme-12 fs-12 fw-medium user-select-none"
                  value={fields[purchasing_email_status].value}
                  onChange={() =>
                    selectField(purchasing_email_status)(!fields[purchasing_email_status].value)
                  }
                />
                <div
                  className={`ms-1 fs-12 fw-medium ${
                    fields[purchasing_email_status].value ? 'text-turquoise' : 'text-timber-wolf'
                  }`}
                >
                  {fields[purchasing_email_status].value ? 'On' : 'Off'}
                </div>
              </div>
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>
    </Category>
  );
};

export default ExternalEmailIntegration;
